import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api, fmtCurrency, fmtDate } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusPill } from '@/components/fintech/StatusPill';
import { Building2, Send, FileText, ArrowDownToLine, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

export default function BusinessDashboardPage() {
  const { company, reloadCompany } = useOutletContext();
  const [batches, setBatches] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [depositCurrency, setDepositCurrency] = useState('USD');
  const [depositAmount, setDepositAmount] = useState('');
  const [deposits, setDeposits] = useState([]);

  useEffect(() => {
    api.get('/b2b/mass-payments').then(r => setBatches(r.data.items || []));
    api.get('/b2b/invoices').then(r => setInvoices(r.data.items || []));
    api.get('/b2b/company/deposits').then(r => setDeposits(r.data.items || []));
  }, []);

  const submitDeposit = async (e) => {
    e.preventDefault();
    const fd = new FormData(); fd.append('currency', depositCurrency); fd.append('amount', String(Number(depositAmount)));
    try {
      await api.post('/b2b/company/deposits', fd);
      toast.success('Company deposit request submitted');
      setDepositAmount('');
      const r = await api.get('/b2b/company/deposits'); setDeposits(r.data.items || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const isApproved = company?.verification_status === 'APPROVED';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">Business dashboard</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{company?.legal_name} · {company?.country}</p>
        </div>
        <div data-testid="b2b-verification-status"><StatusPill status={company?.verification_status || 'PENDING'} /></div>
      </div>

      {!isApproved && (
        <Card className="bg-[hsl(43_74%_94%)] border-[hsl(43_60%_80%)]"><CardContent className="p-4 flex items-center gap-3">
          <Clock className="h-5 w-5 text-[hsl(43_74%_22%)]" />
          <div className="text-sm text-[hsl(43_74%_22%)]">Your company is <strong>{company?.verification_status?.toLowerCase()}</strong>. Mass payments and invoices unlock after verification by our compliance team.</div>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['USD','EUR','KMF','KZT'].map(c => (
          <Card key={c} className="bg-white"><CardContent className="p-5">
            <div className="flex justify-between items-center">
              <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">{c}</div>
              <div className="h-8 w-8 rounded-lg bg-[hsl(var(--brand-gold))]/10 flex items-center justify-center text-xs font-serif text-[hsl(var(--brand-navy))]">{c[0]}</div>
            </div>
            <div className="mt-3 text-2xl font-semibold tabular-nums text-[hsl(var(--brand-navy-2))]">{fmtCurrency(company?.balances?.[c] || 0, c)}</div>
          </CardContent></Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-white"><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3"><ArrowDownToLine className="h-5 w-5 text-[hsl(var(--brand-navy))]" /><div className="font-serif text-lg">Fund company wallet</div></div>
          <form onSubmit={submitDeposit} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Select value={depositCurrency} onValueChange={setDepositCurrency}><SelectTrigger data-testid="b2b-deposit-currency"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="USD">USD</SelectItem><SelectItem value="EUR">EUR</SelectItem><SelectItem value="KMF">KMF</SelectItem><SelectItem value="KZT">KZT</SelectItem></SelectContent></Select>
              <Input type="number" step="0.01" min="0.01" data-testid="b2b-deposit-amount" placeholder="Amount" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} />
            </div>
            <Button type="submit" size="sm" data-testid="b2b-deposit-submit" className="w-full bg-[hsl(var(--brand-navy))]">Submit</Button>
          </form>
          {deposits.length > 0 && (
            <div className="mt-4 space-y-2 text-xs">
              {deposits.slice(0, 3).map(d => (
                <div key={d.id} className="flex justify-between"><span>{d.currency} {fmtCurrency(d.amount, d.currency)}</span><StatusPill status={d.status} /></div>
              ))}
            </div>
          )}
        </CardContent></Card>

        <Card className="bg-white"><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3"><Send className="h-5 w-5 text-[hsl(var(--brand-navy))]" /><div className="font-serif text-lg">Mass payments</div></div>
          <div className="text-3xl font-semibold tabular-nums text-[hsl(var(--brand-navy-2))]">{batches.length}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Total batches</div>
          <Button asChild size="sm" className="mt-4 w-full" variant="outline"><Link to="/business-portal/mass-payments">Open</Link></Button>
        </CardContent></Card>

        <Card className="bg-white"><CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3"><FileText className="h-5 w-5 text-[hsl(var(--brand-navy))]" /><div className="font-serif text-lg">Invoices</div></div>
          <div className="text-3xl font-semibold tabular-nums text-[hsl(var(--brand-navy-2))]">{invoices.length}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))]">Total invoices</div>
          <Button asChild size="sm" className="mt-4 w-full" variant="outline"><Link to="/business-portal/invoices">Open</Link></Button>
        </CardContent></Card>
      </div>

      <Card className="bg-white"><CardContent className="p-6">
        <div className="font-serif text-lg mb-4">Company info</div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div><dt className="text-[hsl(var(--muted-foreground))]">Legal name</dt><dd className="font-medium">{company.legal_name}</dd></div>
          <div><dt className="text-[hsl(var(--muted-foreground))]">Registration N°</dt><dd className="font-medium">{company.registration_number}</dd></div>
          <div><dt className="text-[hsl(var(--muted-foreground))]">Country</dt><dd className="font-medium">{company.country}</dd></div>
          <div><dt className="text-[hsl(var(--muted-foreground))]">Address</dt><dd className="font-medium">{company.address}</dd></div>
          {company.tax_number && <div><dt className="text-[hsl(var(--muted-foreground))]">Tax N°</dt><dd className="font-medium">{company.tax_number}</dd></div>}
          {company.bank_account && <div><dt className="text-[hsl(var(--muted-foreground))]">Bank account</dt><dd className="font-medium">{company.bank_account}</dd></div>}
        </dl>
      </CardContent></Card>
    </div>
  );
}
